import { Box, Center, Spinner } from '@chakra-ui/react'
import { Session } from '@supabase/supabase-js'
import { useEffect, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import ErrorBoundary from './components/ErrorBoundary'
import { OwnersProvider } from './components/features/owners'
import { PropertiesProvider } from './components/features/properties/PropertiesProvider/PropertiesProvider'
import { Login } from './components/Login'
import { SignUp } from './components/SignUp'
import { MainContext, MainContextType } from './contexts/MainContext'
import { DashboardLayout } from './layouts/DashboardLayout'
import { isUserAdmin, supabase } from './lib/supabase'
import { AuditTrailPage } from './pages/audit/AuditTrailPage'
import { InsightsPage } from './pages/dashboard/InsightsPage'
import { OwnerDetailsPage } from './pages/owners/OwnerDetailsPage'
import { OwnersPage } from './pages/owners/OwnersPage'
import PropertiesPage from './pages/properties/PropertiesPage'
import { PropertyDetailsPage } from './pages/properties/PropertyDetailsPage'
import { PropertyFormPage } from './pages/properties/PropertyFormPage'
import { OwnerReportDetailsPage } from './pages/reports/OwnerReportDetailsPage'
import { PayoutReportsLayout } from './pages/reports/PayoutReportsLayout'
import { PayoutReportsPage } from './pages/reports/PayoutReportsPage'
import { ReservationsPage } from './pages/reservations/ReservationsPage'
import { SettingsPage } from './pages/settings/SettingsPage'

function App() {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const [showSignUp, setShowSignUp] = useState(false)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setLoading(false)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
    })

    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (session) {
      isUserAdmin().then((admin) => {
        setIsAdmin(admin)
      })
    } else {
      setIsAdmin(false)
    }
  }, [session])

  if (loading) {
    return (
      <Center minH="100vh" bg="gray.50">
        <Spinner size="xl" color="blue.500" thickness="4px" />
      </Center>
    )
  }

  const mainContextValue: MainContextType = {
    session,
    isAdmin,
  }

  return (
    <MainContext.Provider value={mainContextValue}>
      <BrowserRouter>
        <Routes>
          {!session ? (
            <>
              <Route
                path="/login"
                element={
                  <Box
                    minH="100vh"
                    bg="gray.50"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    py={12}
                    px={4}
                  >
                    {showSignUp ? (
                      <SignUp onBack={() => setShowSignUp(false)} />
                    ) : (
                      <Login onSignUp={() => setShowSignUp(true)} />
                    )}
                  </Box>
                }
              />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </>
          ) : (
            <Route path="/" element={<DashboardLayout />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<InsightsPage />} />
              <Route
                path="properties/*"
                element={
                  <PropertiesProvider>
                    <Routes>
                      <Route
                        index
                        element={
                          <ErrorBoundary fallbackMessage="Could not load the properties page.">
                            <PropertiesPage />
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="new"
                        element={
                          <ErrorBoundary fallbackMessage="Could not load the property form.">
                            <PropertyFormPage />
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="new-stays"
                        element={
                          <ErrorBoundary fallbackMessage="Could not load the property form.">
                            <PropertyFormPage staysSynced />
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="edit/:id"
                        element={
                          <ErrorBoundary fallbackMessage="Could not load the property form.">
                            <PropertyFormPage />
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path=":id"
                        element={
                          <ErrorBoundary fallbackMessage="Could not load the property details.">
                            <PropertyDetailsPage />
                          </ErrorBoundary>
                        }
                      />
                    </Routes>
                  </PropertiesProvider>
                }
              />
              <Route path="reservations" element={<ReservationsPage />} />
              {/* <Route path="clients" element={<ClientsPage />} /> */}
              <Route
                path="owners/*"
                element={
                  <OwnersProvider>
                    <Routes>
                      <Route
                        index
                        element={
                          <ErrorBoundary fallbackMessage="Could not load the owners page.">
                            <OwnersPage />
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path=":id"
                        element={
                          <ErrorBoundary fallbackMessage="Could not load the owner details.">
                            <OwnerDetailsPage />
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="new"
                        element={
                          <ErrorBoundary fallbackMessage="Could not load the new owner form.">
                            <OwnerDetailsPage />
                          </ErrorBoundary>
                        }
                      />
                    </Routes>
                  </OwnersProvider>
                }
              />
              <Route path="reports" element={<PayoutReportsLayout />}>
                <Route index element={<PayoutReportsPage />} />
                <Route
                  path="owner/:ownerId"
                  element={<OwnerReportDetailsPage />}
                />
              </Route>
              {isAdmin && <Route path="settings" element={<SettingsPage />} />}
              {isAdmin && <Route path="audit" element={<AuditTrailPage />} />}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          )}
        </Routes>
      </BrowserRouter>
    </MainContext.Provider>
  )
}

export default App
